import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { useEnquiryForm } from "@/hooks/useEnquiryForm";
import { PROJECT_TYPES, BUDGETS } from "@/data/leads";
import { CONTACT } from "@/data/content";

const fieldCls =
    "w-full border-b border-wood/25 bg-transparent py-3 text-base text-ink placeholder:text-fog focus:border-terra focus:outline-none transition-colors duration-300";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.25em] text-timber";
const SUCCESS_ENTER = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };
const FORM_EXIT = { exit: { opacity: 0 } };

const Field = ({ id, label, children }) => (
    <div>
        <label htmlFor={id} className={labelCls}>{label}</label>
        {children}
    </div>
);

const Select = ({ testid, id, value, onChange, options }) => (
    <select data-testid={testid} id={id} value={value} onChange={onChange} className={`${fieldCls} cursor-pointer`}>
        <option value="">Select…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
);

const Success = ({ compact }) => (
    <motion.div data-testid="enquiry-success" {...SUCCESS_ENTER} className={`flex flex-col items-start gap-5 ${compact ? "py-6" : "py-10"}`}>
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-terra text-ivory">
            <Check size={20} strokeWidth={1.5} />
        </span>
        <h3 className="font-serif text-2xl text-ink sm:text-3xl">Thank you — we've received your enquiry.</h3>
        <p className="max-w-md text-base font-light leading-relaxed text-mute">
            Someone from the studio will call you within one working day. Prefer not to wait?
        </p>
        <a data-testid="enquiry-success-whatsapp" href={CONTACT.whatsappHref} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-terra hover:text-ink">
            WhatsApp us now <ArrowRight size={14} />
        </a>
    </motion.div>
);

const Fields = ({ source, form, set, compact }) => (
    <>
        <Field id={`${source}-name`} label="Your name *">
            <input data-testid="enquiry-name-input" id={`${source}-name`} required minLength={2} maxLength={80} value={form.name} onChange={set("name")} placeholder="Full name" className={fieldCls} />
        </Field>
        <Field id={`${source}-phone`} label="Phone / WhatsApp *">
            <input data-testid="enquiry-phone-input" id={`${source}-phone`} required type="tel" minLength={7} maxLength={20} value={form.phone} onChange={set("phone")} placeholder="+91 …" className={fieldCls} />
        </Field>
        <Field id={`${source}-email`} label="Email">
            <input data-testid="enquiry-email-input" id={`${source}-email`} type="email" maxLength={120} value={form.email} onChange={set("email")} placeholder="you@example.com" className={fieldCls} />
        </Field>
        <Field id={`${source}-type`} label="Project type">
            <Select testid="enquiry-type-select" id={`${source}-type`} value={form.project_type} onChange={set("project_type")} options={PROJECT_TYPES} />
        </Field>
        <Field id={`${source}-budget`} label="Indicative budget">
            <Select testid="enquiry-budget-select" id={`${source}-budget`} value={form.budget} onChange={set("budget")} options={BUDGETS} />
        </Field>
        {!compact && (
            <div className="md:col-span-2">
                <Field id={`${source}-message`} label="Tell us about your space">
                    <textarea data-testid="enquiry-message-input" id={`${source}-message`} rows={3} maxLength={1500} value={form.message} onChange={set("message")} placeholder="Location, size, what you'd like to change, timelines…" className={`${fieldCls} resize-none`} />
                </Field>
            </div>
        )}
        <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden />
    </>
);

const SubmitRow = ({ loading, error }) => (
    <div className="flex flex-wrap items-center gap-6 md:col-span-2">
        <button
            data-testid="enquiry-submit-btn"
            type="submit"
            disabled={loading}
            className="group inline-flex items-center gap-3 rounded-full bg-ink px-8 py-4 text-xs uppercase tracking-[0.2em] text-ivory transition-colors duration-300 hover:bg-terra disabled:opacity-60"
        >
            {loading ? "Sending…" : "Request a Consultation"}
            <span className="h-1.5 w-1.5 rotate-45 bg-gold transition-transform duration-300 group-hover:rotate-[135deg]" />
        </button>
        {error ? (
            <p data-testid="enquiry-error" role="alert" className="text-sm text-terra">{error}</p>
        ) : (
            <p className="text-xs font-light text-fog">We reply within one working day. No spam, ever.</p>
        )}
    </div>
);

export const EnquiryForm = ({ source = "contact", compact = false }) => {
    const { form, set, status, error, submit } = useEnquiryForm(source);

    return (
        <AnimatePresence mode="wait">
            {status === "done" ? (
                <Success key="ok" compact={compact} />
            ) : (
                <motion.form
                    key="form"
                    data-testid={`enquiry-form-${source}`}
                    onSubmit={submit}
                    {...FORM_EXIT}
                    className={`grid grid-cols-1 gap-x-10 md:grid-cols-2 ${compact ? "gap-y-6" : "gap-y-8"}`}
                >
                    <Fields source={source} form={form} set={set} compact={compact} />
                    <SubmitRow loading={status === "loading"} error={error} />
                </motion.form>
            )}
        </AnimatePresence>
    );
};
